import React from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Products } from './pages/Products';
import { Categories } from './pages/Categories';
import { Promotions }   from './pages/Promotions';
import { ProductForm }  from './pages/ProductForm';
import { Orders }       from './pages/Orders';
import { Logs } from './pages/Logs';
import { ToastProvider } from './components/Toast';
import { useAppSelector } from './store/hooks';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const SuperAdminRoute: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user);
  if (user?.role !== 'superadmin') return <Navigate to="/" replace />;
  return <Outlet />;
};

const AdminRoute: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user);
  if (user?.role === 'superadmin') return <Navigate to="/logs" replace />;
  return <Outlet />;
};

const Placeholder: React.FC<{ title: string }> = ({ title }) => (
  <div className="glass-panel p-12 rounded-2xl border border-slate-800/40 text-center space-y-3">
    <h3 className="font-heading font-bold text-lg text-white">{title}</h3>
    <p className="text-slate-400 text-sm max-w-sm mx-auto">
      Esta sección estará disponible próximamente.
    </p>
  </div>
);

export const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 bg-page-bg">
          <p className="font-heading text-lg font-semibold text-ink">Ocurrió un error inesperado</p>
          <p className="text-sm text-ink-muted max-w-sm text-center">
            {error instanceof Error ? error.message : 'Error desconocido'}
          </p>
          <button
            onClick={resetError}
            className="px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-hover transition-colors"
          >
            Reintentar
          </button>
        </div>
      )}
    >
      <BrowserRouter>
        <ToastProvider />
        <SentryRoutes>
          <Route path="/login" element={<Login />} />
          <Route element={<Layout />}>
            <Route element={<AdminRoute />}>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="products/new" element={<ProductForm />} />
              <Route path="products/:id/edit" element={<ProductForm />} />
              <Route path="orders" element={<Orders />} />
              <Route path="categories" element={<Categories />} />
              <Route path="promotions" element={<Promotions />} />
              <Route path="customers" element={<Placeholder title="Clientes" />} />
              <Route path="analytics" element={<Placeholder title="Analíticas" />} />
            </Route>
            <Route element={<SuperAdminRoute />}>
              <Route path="logs" element={<Logs />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </SentryRoutes>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
};

export default App;
